/**
 * Client-side prefilled MAF (v3.7.0) using pdf-lib.
 * Field IDs extracted from public/forms/MAF_v3.7.0_Local_Merchant_D20260907.pdf
 * and mapped in `pdf extract/MAF_v3.7_field_layout_updated_mapped.csv`.
 * Data: step 1 businessEntity, step 2 merchants, step 3 terminals, step 4 fees.
 */

import { PDFDocument, StandardFonts } from "pdf-lib";
import { MAPPED_REGISTERED_COUNTRY } from "./constants";
import api from "../services/api";

const TEMPLATE = `${process.env.PUBLIC_URL || ""}/forms/MAF_v3.7.0_Local_Merchant_D20260907.pdf`;

function str(v) {
  if (v == null || v === false) return "";
  return String(v).trim();
}

function setText(form, fieldName, value) {
  const s = str(value);
  if (!s) return;
  try {
    form.getTextField(fieldName).setText(s);
  } catch {
    console.warn(`MAF v3.7.0: text field missing or wrong type: ${fieldName}`);
  }
}

function setTextForce(form, fieldName, value) {
  try {
    form.getTextField(fieldName).setText(str(value));
  } catch {
    console.warn(`MAF v3.7.0: text field missing: ${fieldName}`);
  }
}

function checkBox(form, fieldName, checked) {
  if (!checked) return;
  try {
    form.getCheckBox(fieldName).check();
  } catch {
    console.warn(`MAF v3.7.0: checkbox missing: ${fieldName}`);
  }
}

function splitLines(value, maxLines = 4, lineWidth = 80) {
  if (!str(value)) return [];
  return String(value)
    .split(/\r?\n/)
    .flatMap((line) => line.match(new RegExp(`.{1,${lineWidth}}(\\s|$)`, "g")) || [line])
    .map((l) => l.trim())
    .filter(Boolean)
    .slice(0, maxLines);
}

function setTextLines(form, fieldNames, value) {
  const lines = splitLines(value, fieldNames.length, 80);
  fieldNames.forEach((fieldName, index) => {
    setTextForce(form, fieldName, lines[index] || "");
  });
}

function chunkBankAddress(text, chunks) {
  const s = str(text);
  if (!s) return chunks.map(() => "");
  const parts = s.split(/\r?\n/).flatMap((line) => {
    const out = [];
    let rest = line;
    while (rest.length > 120) {
      out.push(rest.slice(0, 120));
      rest = rest.slice(120);
    }
    out.push(rest);
    return out;
  });
  return chunks.map((_, i) => str(parts[i]));
}

function countryLabel(registeredCountryId, countryList) {
  if (!registeredCountryId || !Array.isArray(countryList)) return "";
  const row = countryList.find((c) => c.id === registeredCountryId);
  if (!row) return "";
  const key = row.registered_country;
  return MAPPED_REGISTERED_COUNTRY[key] || str(key) || "";
}

function mccDescription(activeBusinessEntity, merchant) {
  const id = merchant?.merchant_category_code;
  if (!id || !activeBusinessEntity?.mcc) return "";
  const row = activeBusinessEntity.mcc.find((m) => m.id === id);
  return row ? str(row.code_description) : "";
}

function productsServicesBlock(be, merchant, activeBusinessEntity) {
  const mcc = mccDescription(activeBusinessEntity, merchant);
  const name = str(merchant?.name);
  const extra = str(be?.products_services);
  return [extra, mcc, name].filter(Boolean).join(" — ");
}

function registeredAddressBlock(be, countryList) {
  const lines = [be?.address_1, be?.address_2, be?.address_3].map((x) => str(x)).filter(Boolean);
  const pc = str(be?.postal_code);
  const cn = countryLabel(be?.registered_country, countryList) || str(be?.address_country);
  const last = [pc, cn].filter(Boolean).join(" ");
  if (last) lines.push(last);
  return lines.join("\n");
}

function outletAddressText(merchant, be, countryList) {
  if (merchant?.address_as_business_entity) {
    return registeredAddressBlock(be, countryList);
  }
  const parts = [
    merchant?.address_line1,
    merchant?.address_line2,
    merchant?.address_line3,
    merchant?.city,
  ]
    .map((x) => str(x))
    .filter(Boolean);
  return parts.join("\n");
}

function principalActivityBlock(be) {
  return str(be?.principal_activity || be?.ssic_description || be?.short_name || be?.business_name);
}

function directorIdTypeChecks(be) {
  const t = str(be?.director_id_type).toUpperCase();
  return {
    nric: t.includes("NRIC"),
    passport: t.includes("PASSPORT"),
    work: t.includes("WORK"),
  };
}

// --- Payment helpers ---

function joinPayLabels(p) {
  return [p?.payment_method_name, p?.payment_brand_name, p?.payment_host_name]
    .filter(Boolean)
    .join(" ")
    .toUpperCase();
}

function terminalPayload(row) {
  if (!row) return null;
  if (row.terminal && typeof row.terminal === "object") return row.terminal;
  return row;
}

function paymentsFromTerminalRow(row) {
  const t = terminalPayload(row);
  const nested = t?.payment;
  if (Array.isArray(nested) && nested.length) return nested;
  if (Array.isArray(row?.payment) && row.payment.length) return row.payment;
  return Array.isArray(nested) ? nested : [];
}

function getPaymentsFromTerminals(terminalsStep) {
  const list = terminalsStep?.terminals;
  if (!Array.isArray(list) || !list.length) return [];
  const out = [];
  for (const row of list) {
    out.push(...paymentsFromTerminalRow(row));
  }
  return out;
}

function getPaymentsFromTerminalsByType(terminalsStep, typeLower) {
  const list = terminalsStep?.terminals;
  if (!Array.isArray(list) || !list.length) return [];
  const out = [];
  for (const row of list) {
    const tt = str(terminalPayload(row)?.terminal_type).toLowerCase();
    if (tt === typeLower) out.push(...paymentsFromTerminalRow(row));
  }
  return out;
}

function anyTerminalHasType(terminalsStep, typeLower) {
  const list = terminalsStep?.terminals;
  if (!Array.isArray(list)) return false;
  for (const row of list) {
    const tt = str(terminalPayload(row)?.terminal_type).toLowerCase();
    if (tt === typeLower) return true;
  }
  return false;
}

function findPayment(payments, ...mustContain) {
  const must = mustContain.map((m) => str(m).toUpperCase()).filter(Boolean);
  for (const p of payments) {
    const lab = joinPayLabels(p);
    if (must.length && must.every((m) => lab.includes(m))) return p;
  }
  for (const p of payments) {
    const lab = joinPayLabels(p);
    if (must.length && must.some((m) => lab.includes(m))) return p;
  }
  return null;
}

function formatTransactionFeeWithCurrency(p) {
  if (!p) return "";
  const fallback = str(p.sales_charges || p.markup_rate);
  const sf = p.service_fees;
  if (!sf || typeof sf !== "object") return fallback;

  const segments = [];
  for (const code of Object.keys(sf).sort()) {
    const bucket = sf[code];
    if (!bucket) continue;
    const tf = bucket.transaction_fee;
    if (tf === undefined || tf === null || tf === "") continue;
    const cur = str(bucket.currency || code);
    const n = typeof tf === "number" ? tf : parseFloat(String(tf).replace(/,/g, ""));
    const amt = Number.isFinite(n) ? String(n) : str(tf);
    if (!amt) continue;
    segments.push(`${amt} ${cur}`);
  }
  return segments.length ? segments.join(" · ") : fallback;
}

function aggregateServiceFeesTransactionFees(payments) {
  if (!Array.isArray(payments) || !payments.length) return "";
  const byCur = new Map();
  for (const p of payments) {
    const sf = p?.service_fees;
    if (!sf || typeof sf !== "object") continue;
    for (const code of Object.keys(sf)) {
      const bucket = sf[code];
      if (!bucket) continue;
      const tf = bucket.transaction_fee;
      if (tf === undefined || tf === null || tf === "") continue;
      const cur = str(bucket.currency || code);
      if (!cur || byCur.has(cur)) continue;
      const n = typeof tf === "number" ? tf : parseFloat(String(tf).replace(/,/g, ""));
      const amt = Number.isFinite(n) ? String(n) : str(tf);
      if (!amt) continue;
      byCur.set(cur, amt);
    }
  }
  if (!byCur.size) return "";
  return [...byCur.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([cur, amt]) => `${amt} ${cur}`)
    .join(" · ");
}

function fillOtherFeesArrangement(form, m0, payments) {
  const ba = m0?.bank_account || {};
  const settlementFee = str(ba.settlement_fee ?? m0?.settlement_fee);
  const txnFeeLine = aggregateServiceFeesTransactionFees(payments);

  if (!settlementFee && !txnFeeLine) return;

  checkBox(form, "checkbox_284grsl", true);
  setText(form, "text_178mbbj", "Settlement Fee (per settlement)");
  setText(form, "text_172rogc", settlementFee);
  setText(form, "text_177oybg", "Transaction Fee");
  setText(form, "text_174saan", txnFeeLine);
}

// Card fee tables in v3.7.0 split the old single "Transaction Fee" column into
// separate Local/Foreign per-transaction fees, sourced from Step 3's
// transaction_fee_local / transaction_fee_foreign card fields.
function cardQuad(p) {
  if (!p) return { local: "", localFee: "", foreign: "", foreignFee: "" };
  return {
    local: str(p.base_rate),
    localFee: str(p.transaction_fee_local),
    foreign: str(p.base_rate_foreign),
    foreignFee: str(p.transaction_fee_foreign),
  };
}

function apmPercent(p) {
  if (!p) return "";
  return str(p.markup_rate || p.base_rate || p.sales_charges);
}

function visaMcPayment(payments) {
  return (
    findPayment(payments, "VISA") ||
    findPayment(payments, "MASTERCARD") ||
    findPayment(payments, "MASTER")
  );
}

function findGrabPayStandard(payments) {
  for (const p of payments) {
    const lab = joinPayLabels(p);
    if (lab.includes("LATER")) continue;
    if (lab.includes("GRAB") && lab.includes("PAY")) return p;
  }
  return null;
}

function findShopeePayStandard(payments) {
  for (const p of payments) {
    const lab = joinPayLabels(p);
    if (lab.includes("LATER")) continue;
    if (lab.includes("SHOPEE")) return p;
  }
  return null;
}

function findShopeeLater(payments) {
  for (const p of payments) {
    const lab = joinPayLabels(p);
    if (!lab.includes("SHOPEE") || !lab.includes("LATER")) continue;
    if (lab.includes("PREMIUM")) continue;
    return p;
  }
  return null;
}

function findShopeePayLaterPremium(payments) {
  for (const p of payments) {
    const lab = joinPayLabels(p);
    if (lab.includes("SHOPEE") && lab.includes("LATER") && lab.includes("PREMIUM")) return p;
    if (lab.includes("SPAYLATER") && lab.includes("PREMIUM")) return p;
    if (lab.includes("SPAY") && lab.includes("PREMIUM")) return p;
  }
  return null;
}

function findGrabLater(payments) {
  return findPayment(payments, "GRAB", "LATER");
}

function findUpiIndia(payments) {
  for (const p of payments) {
    const lab = joinPayLabels(p);
    if (lab.includes("UNION") && lab.includes("INTERNATIONAL")) continue;
    if (lab.includes("INDIA") && lab.includes("UPI")) return p;
    if (lab.includes("UPI") && lab.includes("INDIA")) return p;
  }
  const india = findPayment(payments, "INDIA");
  if (india && joinPayLabels(india).includes("UPI")) return india;
  return null;
}

function hasIppMonths(payments, months) {
  const needle = String(months);
  for (const p of payments) {
    const lab = joinPayLabels(p);
    if (!lab.includes(needle)) continue;
    if (lab.includes("IPP") || lab.includes("INSTALL") || lab.includes("INSTALMENT")) return true;
  }
  return false;
}

function apmIppMonths(payments, months) {
  const needle = String(months);
  for (const p of payments) {
    const lab = joinPayLabels(p);
    if (!lab.includes(needle)) continue;
    if (!(lab.includes("IPP") || lab.includes("INSTALL") || lab.includes("INSTALMENT"))) continue;
    const v = apmPercent(p);
    if (v) return v;
  }
  return "";
}

function minTxnAmount(payments) {
  const pn = findPayment(payments, "PAYNOW");
  if (pn && str(pn.min_amt_postpaid)) return str(pn.min_amt_postpaid);
  for (const p of payments) {
    if (str(p.min_amt_postpaid)) return str(p.min_amt_postpaid);
  }
  return "";
}

function paymentCycleLabel(m0) {
  const pc = m0?.payment_cycle;
  if (pc && typeof pc === "object") return str(pc.label);
  return str(pc);
}

// Step 3's per-section "SETTLEMENT T +" select stores only the option id
// (see Step3Terminal.js `${prefix}.settlement_cycle`); resolve it to a
// display label using the same /bank_account/merchant_payout_info/static
// options list Step 3 uses.
async function fetchSettlementCycleOptions() {
  try {
    const res = await api.get("/bank_account/merchant_payout_info/static");
    if (res?.status === 200 || res?.status === 201) {
      return res.data?.payment_cycle || [];
    }
  } catch {
    // ignore — callers fall back to the Step 2 merchant-level cycle
  }
  return [];
}

function resolveCycleLabel(id, options) {
  if (!id) return "";
  const found = options.find((o) => String(o.id) === String(id));
  return found ? str(found.name || found.label) : str(id);
}

function isOnlineApplication(applicationType) {
  return str(applicationType).toUpperCase().includes("ONLINE");
}

// --- New-format transformer ---
// Converts the new Step3 flat-rates format { merchants: [...] } into the legacy
// { terminals: [...] } shape that all existing PDF helper functions expect.
function transformNewTerminalFormat(terminalsStep) {
  if (!terminalsStep) return terminalsStep;
  // Already old format
  if (Array.isArray(terminalsStep.terminals) && !terminalsStep.merchants) return terminalsStep;
  // New format
  if (!Array.isArray(terminalsStep.merchants)) return terminalsStep;

  const terminals = [];

  for (const merchant of terminalsStep.merchants) {
    const modes =
      merchant.terminal_mode === "both"
        ? ["physical", "online"]
        : [merchant.terminal_mode].filter(Boolean);

    for (const mode of modes) {
      const section = merchant[mode];
      if (!section) continue;
      const payment = [];

      // Cards
      const cardMap = [
        { key: "visa_mc",  brand: "VISA MASTERCARD" },
        { key: "diners",   brand: "DINERS" },
        { key: "amex",     brand: "AMEX" },
        { key: "upi",      brand: "UNIONPAY INTERNATIONAL UPI" },
        { key: "discover", brand: "DISCOVER" },
        { key: "jcb",      brand: "JCB" },
      ];
      for (const { key, brand } of cardMap) {
        const c = section.cards?.[key];
        if (!c?.enabled) continue;
        payment.push({
          payment_method_name: "Card",
          payment_brand_name: brand,
          payment_host_name: "",
          base_rate: c.local_rate,
          base_rate_foreign: c.foreign_rate,
          markup_rate: c.local_rate,
          transaction_fee_local: c.transaction_fee_local,
          transaction_fee_foreign: c.transaction_fee_foreign,
        });
      }

      // APM E-Wallets
      const apmMap = [
        { key: "paynow",    brand: "PAYNOW" },
        { key: "grabpay",   brand: "GRABPAY" },
        { key: "upi_india", brand: "UPI INDIA" },
        { key: "alipay",    brand: "ALIPAY" },
        { key: "spay",      brand: "SHOPEEPAY" },
        { key: "roam_qr",   brand: "ROAMQR" },
        { key: "wechat",    brand: "WECHATPAY" },
        { key: "dash",      brand: "DASH" },
      ];
      for (const { key, brand } of apmMap) {
        const a = section.apm?.[key];
        if (!a?.enabled) continue;
        payment.push({
          payment_method_name: "E-Wallet",
          payment_brand_name: brand,
          payment_host_name: brand.toLowerCase(),
          base_rate: a.rate,
          markup_rate: a.rate,
          sales_charges: "",
        });
      }

      // BNPL
      const bnplMap = [
        { key: "atome",        brand: "ATOME" },
        { key: "grab_later",   brand: "GRAB PAYLATER" },
        { key: "spay_basic",   brand: "SHOPEEPAYLATER" },
        { key: "spay_premium", brand: "SHOPEEPAYLATER PREMIUM" },
      ];
      for (const { key, brand } of bnplMap) {
        const b = section.bnpl?.[key];
        if (!b?.enabled) continue;
        payment.push({
          payment_method_name: "BNPL",
          payment_brand_name: brand,
          payment_host_name: brand.toLowerCase(),
          base_rate: b.rate,
          markup_rate: b.rate,
          sales_charges: b.transaction_fee,
          service_fees: b.transaction_fee
            ? { SGD: { currency: "SGD", transaction_fee: b.transaction_fee } }
            : undefined,
        });
      }

      // IPP
      for (const months of [3, 6, 12, 24]) {
        const k = `months_${months}`;
        const i = section.ipp?.[k];
        if (!i?.enabled) continue;
        payment.push({
          payment_method_name: `IPP`,
          payment_brand_name: `INSTALMENT ${months}`,
          payment_host_name: `ipp-${months}`,
          base_rate: i.rate,
          markup_rate: i.rate,
          sales_charges: "",
          min_amt_postpaid: section.min_transaction_amount || "",
        });
      }

      terminals.push({
        terminal_type: mode === "physical" ? "Offline" : "Online",
        settlement_cycle: section.settlement_cycle,
        payment,
        terminal_fee: merchant.fees?.terminal_fee,
        terminal_fee_currency: merchant.fees?.terminal_fee_currency || "SGD",
        security_deposit: merchant.fees?.security_deposit,
        chargeback_fee: merchant.fees?.chargeback_fee,
        chargeback_fee_currency: merchant.fees?.chargeback_fee_currency || "SGD",
        refund_fee: merchant.fees?.refund_fee,
        refund_fee_currency: merchant.fees?.refund_fee_currency || "SGD",
      });
    }
  }

  return { ...terminalsStep, terminals };
}

/**
 * @param {object} params
 * @param {object} params.businessEntity
 * @param {object} params.merchantsStep `{ items: [...] }`
 * @param {Array} params.countryList
 * @param {object} params.activeBusinessEntity
 * @param {object} [params.terminalsStep] `{ terminals: [...] }` or new `{ merchants: [...] }` step 3
 * @param {object} [params.feesStep] Settlement & Fees data from Step 4
 * @param {string} [params.applicationType] optional override
 */
export async function buildMafV370PdfBytes({
  businessEntity,
  merchantsStep,
  countryList,
  activeBusinessEntity,
  terminalsStep,
  feesStep,
  applicationType: applicationTypeOverride,
}) {
  const be = businessEntity || {};
  const items = merchantsStep?.items || [];
  const m0 = items[0] || {};
  const ba = m0.bank_account || {};
  const applicationType = str(applicationTypeOverride || be.application_type);
  // Support both old { terminals: [] } and new { merchants: [] } step-3 format
  const normalizedTerminalsStep = transformNewTerminalFormat(terminalsStep);
  const payPhysical = getPaymentsFromTerminalsByType(normalizedTerminalsStep, "offline");
  const payOnline   = getPaymentsFromTerminalsByType(normalizedTerminalsStep, "online");
  // Page 3 (retail) uses physical payments; fall back to all if no physical-only data
  const pay = payPhysical.length ? payPhysical : getPaymentsFromTerminals(normalizedTerminalsStep);

  // Settlement T+ comes from Step 3's per-section select; fall back to the
  // Step 2 merchant-level cycle (legacy applications / edit flow) if unset.
  const cycleOptions = await fetchSettlementCycleOptions();
  const physicalTerminal = normalizedTerminalsStep?.terminals?.find(
    (t) => str(t.terminal_type).toLowerCase() === "offline"
  );
  const onlineTerminal = normalizedTerminalsStep?.terminals?.find(
    (t) => str(t.terminal_type).toLowerCase() === "online"
  );
  const retailCycle =
    resolveCycleLabel(physicalTerminal?.settlement_cycle, cycleOptions) || paymentCycleLabel(m0);
  const ecommCycle =
    resolveCycleLabel(onlineTerminal?.settlement_cycle, cycleOptions) || paymentCycleLabel(m0);

  const res = await fetch(TEMPLATE);
  if (!res.ok) {
    throw new Error(
      `Cannot load MAF template (${TEMPLATE}). Ensure public/forms/MAF_v3.7.0_Local_Merchant_D20260907.pdf exists.`
    );
  }
  const raw = await res.arrayBuffer();
  const pdfDoc = await PDFDocument.load(raw);
  const form = pdfDoc.getForm();
  const font = await pdfDoc.embedFont(StandardFonts.Helvetica);

  // --- Page 1: Company / Director / Contact / Business Info ---
  setText(form, "text_4suti", be.business_name);
  setText(form, "text_20fqpm", be.uen);
  setText(form, "text_21cpla", be.date_of_incorporation || be.date_of_registration);

  setTextLines(form, ["text_5mipy", "text_6cjfm", "text_7jnvp", "text_8lqdz"], registeredAddressBlock(be, countryList));

  setTextLines(
    form,
    ["text_9yoba", "text_10myar", "text_11asyj", "text_12vdfn"],
    principalActivityBlock(be)
  );

  setText(form, "text_13uomc", be.director_name || be.contact_name);
  const idc = directorIdTypeChecks(be);
  checkBox(form, "checkbox_212fjdh", idc.nric);
  checkBox(form, "checkbox_214ylsg", idc.passport);
  checkBox(form, "checkbox_215pjrb", idc.work);
  setText(form, "text_22eiqs", be.director_citizenship || countryLabel(be.registered_country, countryList));
  setText(form, "text_23gwws", be.director_phone || be.contact_number);
  setText(form, "text_14mpoz", be.director_email || be.contact_email);

  setText(form, "text_15cqxc", m0.name || be.contact_name);
  setText(form, "text_24kbaf", m0.position || be.contact_position);
  setText(form, "text_25efe", be.contact_number);
  setText(form, "text_16ngsg", be.contact_email);
  setText(form, "text_17rmvv", be.contact_email_secondary);

  setTextLines(form, ["text_18ldps", "text_19fzcz"], productsServicesBlock(be, m0, activeBusinessEntity));
  setText(
    form,
    "text_26inbh",
    be.country_of_operations || countryLabel(be.registered_country, countryList) || be.address_country
  );
  setText(form, "text_28wmzg",
    str(m0.average_ticket_size)
      ? `${str(m0.average_ticket_size)} ${str(m0.average_ticket_size_currency) || 'SGD'}`
      : ''
  );

  const fulfil = str(be.products_fulfilment).toLowerCase();
  const delayedPct = str(be.fulfilment_delayed_pct);
  const delayedDays = str(be.fulfilment_longest_cycle_days);
  if (fulfil === "immediate") checkBox(form, "checkbox_216dkpb", true);
  if (fulfil === "delayed") checkBox(form, "checkbox_217oqzg", true);
  if (!fulfil && (delayedPct || delayedDays)) checkBox(form, "checkbox_217oqzg", true);
  setText(form, "text_29zwsl", delayedPct);
  setText(form, "text_31rfwb", delayedDays);

  // --- Page 2: Outlets + Bank ---
  const outlets = [0, 1, 2, 3].map((i) => items[i] || {});
  const outletFields = [
    {
      sameChk: "checkbox_218drtb",
      name: "text_32gdcz",
      addr: ["text_33jkzd", "text_34twbu"],
      term: "text_51qqke",
    },
    {
      name: "text_35iwuf",
      addr: ["text_36faqe", "text_37aqdk"],
      term: "text_52jeji",
    },
    {
      name: "text_38hjau",
      addr: ["text_39fkse", "text_40roiq"],
      term: "text_53bpkq",
    },
    {
      name: "text_41wtzp",
      addr: ["text_42sids", "text_43zgux"],
      term: "text_54xhmo",
    },
  ];

  checkBox(form, outletFields[0].sameChk, Boolean(outlets[0].address_as_business_entity));

  outlets.forEach((mer, idx) => {
    const spec = outletFields[idx];
    if (!spec) return;
    setText(form, spec.name, mer.name);
    setTextLines(form, spec.addr, outletAddressText(mer, be, countryList));
    setText(form, spec.term, mer.number_of_terminals);
  });

  setText(form, "text_44rizl", ba.account_name);
  setText(form, "text_45gbpd", ba.account_number);
  setText(form, "text_46xbvu", ba.bank);
  setText(form, "text_55qwmd", ba.bic_swift_code);
  setText(form, "text_56ftaq", ba.bank_branch_code);

  const bankChunks = chunkBankAddress(ba.bank_address, ["text_47prf", "text_48nnhq", "text_49kqnz"]);
  ["text_47prf", "text_48nnhq", "text_49kqnz"].forEach((fn, i) => setTextForce(form, fn, bankChunks[i]));

  setText(form, "text_50dfse", ba.intermediary_bank);
  setText(form, "text_57lfsa", ba.intermediary_bank_code);

  // --- Page 3: Retail terminal type + card rates + APM ---
  if (anyTerminalHasType(normalizedTerminalsStep, "offline")) checkBox(form, "checkbox_219ecn", true);
  if (anyTerminalHasType(normalizedTerminalsStep, "online")) checkBox(form, "checkbox_221rkqa", true);

  setText(form, "text_108iqyv", retailCycle); // retail cards settlement T+
  setText(form, "text_109xcss", retailCycle); // retail APM settlement T+

  const vm = visaMcPayment(pay);
  const vmQ = cardQuad(vm);
  setText(form, "text_58qugs", vmQ.local);
  setText(form, "text_59pgvu", vmQ.localFee);
  setText(form, "text_60qsqu", vmQ.foreign);
  setText(form, "text_61tuxn", vmQ.foreignFee);
  checkBox(form, "checkbox_222ofrs", Boolean(vm));

  const din = findPayment(pay, "DINER");
  const dinQ = cardQuad(din);
  setText(form, "text_62moco", dinQ.local);
  setText(form, "text_63erah", dinQ.localFee);
  setText(form, "text_64lppx", dinQ.foreign);
  setText(form, "text_65abwe", dinQ.foreignFee);
  checkBox(form, "checkbox_223zvzt", Boolean(din));

  const am = findPayment(pay, "AMEX");
  const amQ = cardQuad(am);
  setText(form, "text_66llpi", amQ.local);
  setText(form, "text_67zdcc", amQ.localFee);
  setText(form, "text_68yris", amQ.foreign);
  setText(form, "text_69qdcs", amQ.foreignFee);
  checkBox(form, "checkbox_224bkex", Boolean(am));

  const up =
    findPayment(pay, "UNION", "UPI") || findPayment(pay, "UNIONPAY") || findPayment(pay, "UPI");
  const upQ = cardQuad(up);
  setText(form, "text_70pryx", upQ.local);
  setText(form, "text_71ieha", upQ.localFee);
  setText(form, "text_72hjay", upQ.foreign);
  setText(form, "text_73inki", upQ.foreignFee);
  checkBox(form, "checkbox_225xuqw", Boolean(up));

  const disc = findPayment(pay, "DISCOVER");
  const discQ = cardQuad(disc);
  setText(form, "text_74lfqm", discQ.local);
  setText(form, "text_76qyzh", discQ.localFee);
  setText(form, "text_78itqj", discQ.foreign);
  setText(form, "text_79bbmc", discQ.foreignFee);
  checkBox(form, "checkbox_226wufd", Boolean(disc));

  const jcb = findPayment(pay, "JCB");
  const jcbQ = cardQuad(jcb);
  setText(form, "text_75jdfp", jcbQ.local);
  setText(form, "text_77moch", jcbQ.localFee);
  setText(form, "text_81hagd", jcbQ.foreign);
  setText(form, "text_80gnqd", jcbQ.foreignFee);
  checkBox(form, "checkbox_227ikpe", Boolean(jcb));

  // Retail APM
  setText(form, "text_115yijh", apmPercent(findPayment(pay, "PAYNOW")));
  setText(form, "text_116dekn", apmPercent(findGrabPayStandard(pay)));
  const upiIndia = findUpiIndia(pay);
  setText(form, "text_117jwya", apmPercent(upiIndia));
  checkBox(form, "checkbox_228aozx", Boolean(findPayment(pay, "PAYNOW")));
  checkBox(form, "checkbox_231pvlu", Boolean(findGrabPayStandard(pay)));
  checkBox(form, "checkbox_234gxlv", Boolean(upiIndia));

  setText(form, "text_118ubir", apmPercent(findPayment(pay, "ALIPAY")));
  setText(form, "text_119hgzh", apmPercent(findShopeePayStandard(pay)));
  setText(form, "text_120olan", apmPercent(findPayment(pay, "ROAM")));
  checkBox(form, "checkbox_229getf", Boolean(findPayment(pay, "ALIPAY")));
  checkBox(form, "checkbox_235yoee", Boolean(findPayment(pay, "ROAM")));
  checkBox(form, "checkbox_232acfd", Boolean(findShopeePayStandard(pay)));

  setText(form, "text_121smkc", apmPercent(findPayment(pay, "WECHAT")));
  setText(form, "text_122iyyn", apmPercent(findPayment(pay, "DASH")));
  checkBox(form, "checkbox_230uqzy", Boolean(findPayment(pay, "WECHAT")));
  checkBox(form, "checkbox_233deud", Boolean(findPayment(pay, "DASH")));

  const atome = findPayment(pay, "ATOME");
  setText(form, "text_131wbcl", apmPercent(atome));
  setText(form, "text_132once", formatTransactionFeeWithCurrency(atome));
  checkBox(form, "checkbox_236vmzl", Boolean(atome));

  const grabLater = findGrabLater(pay);
  setText(form, "text_136dgav", apmPercent(grabLater));
  setText(form, "text_133tah", formatTransactionFeeWithCurrency(grabLater));
  checkBox(form, "checkbox_237ionu", Boolean(grabLater));

  const shopeeLaterBasic = findShopeeLater(pay);
  setText(form, "text_137gnnv", apmPercent(shopeeLaterBasic));
  setText(form, "text_134gtni", formatTransactionFeeWithCurrency(shopeeLaterBasic));
  checkBox(form, "checkbox_238les", Boolean(shopeeLaterBasic));

  const shopeeLaterPremium = findShopeePayLaterPremium(pay);
  setText(form, "text_138vgca", apmPercent(shopeeLaterPremium));
  setText(form, "text_135lqac", formatTransactionFeeWithCurrency(shopeeLaterPremium));
  checkBox(form, "checkbox_239fcqf", Boolean(shopeeLaterPremium));

  // Retail IPP (3, 6, 12, 24 months)
  setText(form, "text_147fmmf", apmIppMonths(pay, 3));
  checkBox(form, "checkbox_240xnes", hasIppMonths(pay, 3));
  setText(form, "text_148qqtt", apmIppMonths(pay, 6));
  checkBox(form, "checkbox_241zfae", hasIppMonths(pay, 6));
  setText(form, "text_149lqfd", apmIppMonths(pay, 12));
  checkBox(form, "checkbox_242ocyi", hasIppMonths(pay, 12));
  setText(form, "text_150twxo", apmIppMonths(pay, 24));
  checkBox(form, "checkbox_243hebm", hasIppMonths(pay, 24));

  setText(form, "text_156idch", minTxnAmount(pay));

  // --- Page 4: Website / PCI / E-commerce ---
  setText(form, "text_157dtdn", be.website_target_live_date);
  if (be.website_available === true) checkBox(form, "checkbox_244rbcj", true);
  if (be.website_available === false) checkBox(form, "checkbox_246snsm", true);
  setText(form, "text_158fkqd", be.cms_other_text);
  const cms = str(be.cms).toUpperCase();
  checkBox(form, "checkbox_245xgyp", cms.includes("WORDPRESS"));
  checkBox(form, "checkbox_247pcia", cms.includes("WOOCOMMERCE"));
  checkBox(form, "checkbox_249iwry", cms.includes("MAGENTO"));
  checkBox(form, "checkbox_250qtwr", cms === "OTHERS" || cms === "OTHER");
  setText(form, "text_159jkiz", be.website_url);
  if (be.stores_card_details === true) checkBox(form, "checkbox_251ydfm", true);
  if (be.stores_card_details === false) checkBox(form, "checkbox_253roqc", true);
  setText(form, "text_160ewlq", be.pci_target_compliance_date);
  if (be.pci_compliant === true) checkBox(form, "checkbox_252wvir", true);
  if (be.pci_compliant === false) checkBox(form, "checkbox_254talu", true);

  checkBox(form, "checkbox_255gfsj", isOnlineApplication(applicationType));

  // Only fill e-commerce rates when an online terminal exists
  if (payOnline.length) {
  const payE = payOnline;
  setText(form, "text_110dlgl", ecommCycle); // e-comm cards settlement T+
  setText(form, "text_111cjcs", ecommCycle); // e-comm APM settlement T+

  const vmE = visaMcPayment(payE);
  const vmQE = cardQuad(vmE);
  setText(form, "text_82nirl", vmQE.local);
  setText(form, "text_311mvvb", vmQE.localFee);
  setText(form, "text_315aiig", vmQE.foreign);
  setText(form, "text_83xnxh", vmQE.foreignFee);
  checkBox(form, "checkbox_256qgdl", Boolean(vmE));

  const dinE = findPayment(payE, "DINER");
  const dinQE = cardQuad(dinE);
  setText(form, "text_89pw", dinQE.local);
  setText(form, "text_312kida", dinQE.localFee);
  setText(form, "text_316lbkb", dinQE.foreign);
  setText(form, "text_86aisr", dinQE.foreignFee);
  checkBox(form, "checkbox_257voob", Boolean(dinE));

  const amE = findPayment(payE, "AMEX");
  const amQE = cardQuad(amE);
  setText(form, "text_321ixrz", amQE.local);
  setText(form, "text_313vlpx", amQE.localFee);
  setText(form, "text_317xuue", amQE.foreign);
  setText(form, "text_90qlmc", amQE.foreignFee);
  checkBox(form, "checkbox_258wgxb", Boolean(amE));

  const upE = findPayment(payE, "UNION", "UPI") || findPayment(payE, "UNIONPAY") || findPayment(payE, "UPI");
  const upQE = cardQuad(upE);
  setText(form, "text_322grpn", upQE.local);
  setText(form, "text_314mzyg", upQE.localFee);
  setText(form, "text_318vkxq", upQE.foreign);
  setText(form, "text_96yzop", upQE.foreignFee);
  checkBox(form, "checkbox_259ijga", Boolean(upE));

  const discE = findPayment(payE, "DISCOVER");
  const discQE = cardQuad(discE);
  setText(form, "text_323tqkx", discQE.local);
  setText(form, "text_324ugyt", discQE.localFee);
  setText(form, "text_319cuqd", discQE.foreign);
  setText(form, "text_100rgtt", discQE.foreignFee);
  checkBox(form, "checkbox_260tlwv", Boolean(discE));

  const jcbE = findPayment(payE, "JCB");
  const jcbQE = cardQuad(jcbE);
  setText(form, "text_326fvyu", jcbQE.local);
  setText(form, "text_325hvca", jcbQE.localFee);
  setText(form, "text_320ezxy", jcbQE.foreign);
  setText(form, "text_104osuk", jcbQE.foreignFee);
  checkBox(form, "checkbox_261rlsa", Boolean(jcbE));

  // E-commerce APM (no separate RoamQR row in v3.7.0 — UPI (India) takes its place)
  setText(form, "text_123hifb", apmPercent(findPayment(payE, "PAYNOW")));
  setText(form, "text_125crja", apmPercent(findGrabPayStandard(payE)));
  const upiIndiaE = findUpiIndia(payE);
  setText(form, "text_126hhnu", apmPercent(upiIndiaE));
  checkBox(form, "checkbox_262edfi", Boolean(findPayment(payE, "PAYNOW")));
  checkBox(form, "checkbox_265vymh", Boolean(findGrabPayStandard(payE)));
  checkBox(form, "checkbox_268ximf", Boolean(upiIndiaE));

  setText(form, "text_127flcd", apmPercent(findPayment(payE, "ALIPAY")));
  setText(form, "text_128rrnb", apmPercent(findShopeePayStandard(payE)));
  checkBox(form, "checkbox_263nrcs", Boolean(findPayment(payE, "ALIPAY")));
  checkBox(form, "checkbox_266rtjh", Boolean(findShopeePayStandard(payE)));

  setText(form, "text_129fjts", apmPercent(findPayment(payE, "WECHAT")));
  setText(form, "text_130efmx", apmPercent(findPayment(payE, "DASH")));
  checkBox(form, "checkbox_264zeia", Boolean(findPayment(payE, "WECHAT")));
  checkBox(form, "checkbox_267vhqz", Boolean(findPayment(payE, "DASH")));

  const atomeE = findPayment(payE, "ATOME");
  setText(form, "text_139zihw", apmPercent(atomeE));
  setText(form, "text_143qcm", formatTransactionFeeWithCurrency(atomeE));
  checkBox(form, "checkbox_269ycbf", Boolean(atomeE));

  const grabLaterE = findGrabLater(payE);
  setText(form, "text_140qhln", apmPercent(grabLaterE));
  setText(form, "text_144rlyh", formatTransactionFeeWithCurrency(grabLaterE));
  checkBox(form, "checkbox_270evto", Boolean(grabLaterE));

  const shopeeLaterBasicE = findShopeeLater(payE);
  setText(form, "text_141ghnj", apmPercent(shopeeLaterBasicE));
  setText(form, "text_145thsn", formatTransactionFeeWithCurrency(shopeeLaterBasicE));
  checkBox(form, "checkbox_271kxou", Boolean(shopeeLaterBasicE));

  const shopeeLaterPremiumE = findShopeePayLaterPremium(payE);
  setText(form, "text_142cagm", apmPercent(shopeeLaterPremiumE));
  setText(form, "text_146eafl", formatTransactionFeeWithCurrency(shopeeLaterPremiumE));
  checkBox(form, "checkbox_272mfmd", Boolean(shopeeLaterPremiumE));

  // E-commerce IPP (3, 6, 12, 24 months)
  setText(form, "text_151aswm", apmIppMonths(payE, 3));
  checkBox(form, "checkbox_273slup", hasIppMonths(payE, 3));
  setText(form, "text_152agbx", apmIppMonths(payE, 6));
  checkBox(form, "checkbox_274ztk", hasIppMonths(payE, 6));
  setText(form, "text_153ivrp", apmIppMonths(payE, 12));
  checkBox(form, "checkbox_275ogof", hasIppMonths(payE, 12));
  setText(form, "text_154ptjj", apmIppMonths(payE, 24));
  checkBox(form, "checkbox_276tinh", hasIppMonths(payE, 24));

  setText(form, "text_155qvu", minTxnAmount(payE));
  } // end online-only block

  // --- Page 5: Fees (from Step 4 Settlement & Fees form) ---
  const fees = feesStep || {};

  // Annual Fee (top-left; Reprogramming Fee is on the same row, top-right)
  checkBox(form, "checkbox_277mscu", Boolean(fees.annual_fee_enabled));
  setText(form, "text_161piyr", str(fees.annual_fee));

  // Reprogramming Fee (top-right, same row as Annual Fee)
  checkBox(form, "checkbox_281yzqp", Boolean(fees.reprogramming_fee_enabled));
  setText(form, "text_165bzdj", str(fees.reprogramming_fee));

  // Set-up Fee
  checkBox(form, "checkbox_278zqju", Boolean(fees.setup_fee_enabled));
  setText(form, "text_162ahbg", str(fees.setup_fee));

  // Chargeback Fee
  checkBox(form, "checkbox_279xpst", Boolean(fees.chargeback_fee_enabled));
  setText(form, "text_163wmqr", str(fees.chargeback_fee));

  // Refund
  checkBox(form, "checkbox_280dits", Boolean(fees.refund_fee_enabled));
  setText(form, "text_164opmp", str(fees.refund_fee));

  // Physical Terminal
  checkBox(form, "checkbox_282txxc", Boolean(fees.physical_terminal_enabled));
  setText(form, "text_166vnbx", str(fees.physical_terminal_per_unit));
  setText(form, "text_170bisw", str(fees.physical_terminal_num_units));
  setText(form, "text_171mime", str(fees.physical_terminal_total));

  // Limited Time Offer: Refundable Security Deposit for terminal(s)
  if (fees.security_deposit_terminal_enabled) {
    checkBox(form, "checkbox_283ejgh", true);
    setText(form, "text_169srga", str(fees.security_deposit_terminal));
  }

  // Other Fees — rows 1/2/3: title fields text_178mbbj/text_177oybg/text_179dxpe,
  // amount fields text_172rogc/text_174saan/text_175isxr
  if (fees.other_fees_enabled && Array.isArray(fees.other_fees)) {
    checkBox(form, "checkbox_284grsl", true);
    const rows = fees.other_fees;
    if (rows[0]) { setText(form, "text_178mbbj", str(rows[0].description)); setText(form, "text_172rogc", str(rows[0].amount)); }
    if (rows[1]) { setText(form, "text_177oybg", str(rows[1].description)); setText(form, "text_174saan", str(rows[1].amount)); }
    if (rows[2]) { setText(form, "text_179dxpe", str(rows[2].description)); setText(form, "text_175isxr", str(rows[2].amount)); }
  }

  // Settlement Cycle
  if (fees.settlement_cycle_enabled) {
    checkBox(form, "checkbox_285ejde", true);
    if (fees.settlement_cycle_type === "daily") checkBox(form, "checkbox_288lkjx", true);
    else if (fees.settlement_cycle_type === "weekly") checkBox(form, "checkbox_290dthm", true);
    else if (fees.settlement_cycle_type === "others") {
      checkBox(form, "checkbox_287rdit", true);
      setText(form, "text_180ygwt", str(fees.settlement_cycle_others));
    }
  }

  // Security Deposit (subject to periodic review)
  if (fees.security_deposit_enabled) {
    checkBox(form, "checkbox_286ouaj", true);
    setText(form, "text_181lryj", str(fees.security_deposit_percentage));
    setText(form, "text_182jkxz", str(fees.security_deposit_projected_revenue));
  }

  // --- Page 6: left blank (internal use) ---

  form.updateFieldAppearances(font);

  return pdfDoc.save();
}

function sumTerminalUnits(merchantItems, terminals) {
  const fromMerchants = merchantItems.reduce((acc, m) => acc + (Number(m.number_of_terminals) || 0), 0);
  if (fromMerchants > 0) return String(fromMerchants);
  if (terminals?.length) return String(terminals.length);
  return "";
}

function sumTerminalFees(terminals) {
  if (!Array.isArray(terminals)) return "";
  let sum = 0;
  let any = false;
  for (const t of terminals) {
    const v = parseFloat(String(t.terminal_fee || "").replace(/,/g, ""));
    if (!Number.isNaN(v)) {
      sum += v;
      any = true;
    }
  }
  return any ? String(sum) : "";
}

function downloadBlob(bytes, fileName) {
  const blob = new Blob([bytes], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
}

export async function downloadMafV370Pdf(params, fileName = "MAF_prefilled.pdf") {
  const bytes = await buildMafV370PdfBytes(params);
  downloadBlob(bytes, fileName);
}
